import {
	GetPublicationsResponse,
	SubmitPublicationRatingResponse
} from '@kemu-io/kemu-types';
import { ValidPlatformArch } from '@kemu-io/hs-types';
import { clerkApiInterceptor } from '../authenticatedApi/clerkApiInterceptor';
import globals from '@common/globals';

// Forces to return the entire Axios response object instead of only response.data
const returnAxiosResponse = { response: true };

export type GetServiceDocsResponse = {
  docs: string;
  author: {
    name: string;
    organizationId: string;
    organizationName: string;
  };
};

/**
 * Gets a list of publications
 * @param from the index of the first publication to return
 * @param total the number of publications to return
 * @param query an optional query string to filter the publications
 * @returns a list of publications
 */
const getPublications = async (config: {
	from: number,
	total: number,
	platform: ValidPlatformArch,
	query?: string,
	/** list of publication name to return (eg. 'kemu.io.http, kemu.io.mqtt'). If provided, the version and other filter types are ignored */
	names?: string[],
	version?: string,
	markdown?: boolean,
}): Promise<GetPublicationsResponse> => {
	const { from, total, platform, query='*', version, markdown, names } = config;
	let url = `/v1/publications/${platform}?from=${from}&total=${total}&q=${query}`;
	if (version) {
		url += `&version=${version}`;
	}
	if (markdown) {
		url += `&markdown=${markdown}`;
	}

	if (names) {
		url += `&names=${names.join(',')}`;
	}

	const recipe = await clerkApiInterceptor.get(globals.PLATFORM_API_NAME, url, returnAxiosResponse);
	return recipe.data;
};

/**
 * Rates a publication
 * @param config 
 * @returns the updated publication stats
 */
const ratePublication = async (config: {
	publicationId: string,
	rating: number,
	comment?: string,
}): Promise<SubmitPublicationRatingResponse> => {
	const { publicationId, rating, comment } = config;
	const response = await clerkApiInterceptor.post(globals.PLATFORM_API_NAME, `/v1/publications/${publicationId}/rating`, {
		...returnAxiosResponse,
		body: {
			score: rating,
			comment: comment || ''
		}
	});
	const ratingResponse = response.data as SubmitPublicationRatingResponse;
	return ratingResponse;
};

/**
 * Gets the docs for a service
 * @param config 
 * @returns the docs for the service
 */
const getServiceDocs = async (config: {
	name: string,
	version: string,
	platform?: ValidPlatformArch,
}): Promise<GetServiceDocsResponse> => {
	const { name, version } = config;
	const response = await clerkApiInterceptor.get(globals.PLATFORM_API_NAME, `/v1/hub-service/${name}/${version}/docs`, returnAxiosResponse);
	const docsResponse = response.data as GetServiceDocsResponse;
	return docsResponse;
};

export default {
	getServiceDocs,
	ratePublication,
	getPublications,
};
